/**
 * Centralized error handling utilities
 */

/**
 * Extracts user-friendly error message from various error formats
 * @param {Error|Object} error - The error object
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error) => {
  // Network error (no response from server)
  if (!error.response) {
    if (error.request) {
      return 'Unable to connect to server. Please check your internet connection.';
    }
    return error.message || 'An unexpected error occurred.';
  }

  // HTTP error responses
  const { status, data } = error.response;

  // Client errors with API response
  if (data) {
    // Check for validation errors array first (more specific)
    if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
      // Join multiple error messages
      return data.errors.join(', ');
    }

    // New API format: { status, statusCode, timestamp, message, path, errors }
    if (data.message) {
      return data.message;
    }

    // Legacy API error formats
    if (data.apiError) {
      if (typeof data.apiError === 'string') {
        return data.apiError;
      }
      if (data.apiError.errorMessage) {
        return data.apiError.errorMessage;
      }
    }
    if (data.error) {
      return data.error;
    }
    if (data.msg) {
      return data.msg;
    }
  }

  // Default messages based on status code
  // Unauthorized/Forbidden
  if (status === 401) {
    return 'Authentication failed. Please log in again.';
  }
  if (status === 403) {
    return 'You do not have permission to perform this action.';
  }

  // Server errors
  if (status >= 500) {
    return 'Server error. Please try again later.';
  }

  // Bad request
  if (status === 400) {
    return 'Invalid request. Please check your input.';
  }

  return 'An error occurred. Please try again.';
};

/**
 * Checks if error is an authentication error (401/403)
 * @param {Error|Object} error - The error object
 * @returns {boolean}
 */
export const isAuthError = (error) => {
  if (!error.response) return false;
  const status = error.response.status;
  return status === 401 || status === 403;
};

/**
 * Logs error to console with context
 * @param {string} context - Context where error occurred
 * @param {Error|Object} error - The error object
 */
export const logError = (context, error) => {
  const verbose =
    (typeof process !== 'undefined' &&
      process.env &&
      process.env.DEBUG === 'true') ||
    (typeof window !== 'undefined' && window.DEBUG === true);
  const headers = error?.response?.headers || {};
  const data = error?.response?.data || {};
  const correlationId =
    data?.correlationId ||
    headers['x-correlation-id'] ||
    headers['X-Correlation-Id'];
  const baseMsg = correlationId
    ? `[${context}] Error (cid=${correlationId}):`
    : `[${context}] Error:`;
  console.error(baseMsg, error);
  if (verbose && error.response) {
    console.error(`Status: ${error.response.status}`);
    console.error('Data:', error.response.data);
  }
  if (verbose && error.config) {
    console.error(`URL: ${error.config.url}`);
    console.error(`Method: ${error.config.method}`);
  }
};

/**
 * Creates standardized error dispatch object
 * @param {Error|Object} error - The error object
 * @param {string} context - Context where error occurred
 * @returns {Object} Error dispatch object
 */
export const createErrorDispatch = (error, context = '') => {
  logError(context, error);

  const status = error.response?.status;
  const url = error.config?.url || error.response?.config?.url || '';
  const method = error.config?.method || error.response?.config?.method || '';

  // Prefer backend-provided correlation id when available, otherwise generate one
  const data = error.response?.data || {};
  const headers = error.response?.headers || {};

  // New standardized API error shape support
  // export interface ApiErrorResponse {
  //   apiName: string;
  //   status: string | number;
  //   timestamp: string;
  //   errorMessage: string;
  //   correlationId?: string;
  // }
  const correlationIdFromBody = data.correlationId;
  const correlationIdFromHeader =
    headers['x-correlation-id'] || headers['X-Correlation-Id'];

  // Only use backend-provided correlation IDs - don't generate random ones for frontend errors
  // as they aren't logged anywhere and aren't useful for support
  const refId = correlationIdFromBody || correlationIdFromHeader || null;

  const shortMessage = getErrorMessage(error);

  // What we log / store for support (can contain raw backend details)
  const errorMessage = shortMessage;

  // Clean, standardized user-facing message based on error type
  // Provide context-appropriate messages for different error scenarios
  const getUserFriendlyMessage = () => {
    // Network errors (no response from server)
    if (!error.response) {
      if (error.request) {
        return 'Unable to connect to the server. Please check your internet connection and try again.';
      }
      return 'An unexpected error occurred. Please try again. If the problem continues, please contact support.';
    }

    // Auth errors (401/403) - even if backend doesn't send a body
    if (status === 401) {
      return 'Your session has expired. Please log in again to continue.';
    }
    if (status === 403) {
      return "You don't have permission to perform this action. Please contact your administrator if you believe this is an error.";
    }

    // Server errors (5xx)
    if (status >= 500) {
      return refId
        ? "We're experiencing technical difficulties. Please try again later or contact support with the reference ID below."
        : "We're experiencing technical difficulties. Please try again later. If the problem continues, please contact support.";
    }

    // Client errors (4xx) - validation, bad request, etc.
    if (status >= 400 && status < 500) {
      return refId
        ? 'There was a problem with your submission. Please review your entries and try again, or contact support with the reference ID below.'
        : 'There was a problem with your submission. Please review your entries and try again. If the problem continues, please contact support.';
    }

    // Default fallback
    return refId
      ? 'We ran into a problem submitting your form. Please try again or contact support with the reference ID below.'
      : 'Something went wrong while submitting your form. Please check your entries and try again. If the problem continues, please contact support.';
  };

  const userFriendlyMessage = getUserFriendlyMessage();

  return {
    // Technical message for logs/support
    errorMessage,
    // Machine-friendly status/code
    errorCode: status || 'UNKNOWN',
    // Fields for improved support reporting
    userFriendlyMessage,
    errorReference: refId, // Will be null for frontend-only errors
    errorEndpoint: url,
    errorMethod: method,
    submitFailed: true,
    submitStatus: false,
    loading: false,
  };
};

/**
 * Handles API errors consistently
 * @param {Error|Object} error - The error object
 * @param {Function} dispatch - Redux dispatch function
 * @param {string} errorType - Error action type
 * @param {Function} navigate - Navigation function (optional)
 * @param {string} context - Context for logging
 */
export const handleApiError = (
  error,
  dispatch,
  errorType,
  navigate = null,
  context = ''
) => {
  const errorDispatch = createErrorDispatch(error, context);

  // Handle authentication errors
  if (isAuthError(error)) {
    dispatch({
      type: 'GET_LOGOUT',
      auth: null,
      logoutType: 'timed-out',
      logoutMsg: 'Your session has timed out',
    });
    if (navigate) {
      navigate('/logout');
    }
    return;
  }

  // Dispatch the error
  dispatch({
    type: errorType,
    ...errorDispatch,
  });
};

/**
 * Safe axios request wrapper with consistent error handling
 * @param {Function} requestFn - Async function that makes the axios request
 * @param {string} context - Context for error logging
 * @returns {Object} { data, error } - Either data or error will be populated
 */
export const safeAxiosRequest = async (requestFn, context = 'API Request') => {
  try {
    const response = await requestFn();
    return { data: response.data, error: null };
  } catch (error) {
    logError(context, error);
    return { data: null, error };
  }
};

/**
 * Validates required fields and returns errors
 * @param {Object} data - Data object to validate
 * @param {Array<string>} requiredFields - Array of required field names
 * @returns {Array<string>} Array of missing field names
 */
export const validateRequiredFields = (data, requiredFields) => {
  const missing = [];
  requiredFields.forEach((field) => {
    const value = field.split('.').reduce((obj, key) => obj?.[key], data);
    if (value === undefined || value === null || value === '') {
      missing.push(field);
    }
  });
  return missing;
};
